import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, Subject } from 'rxjs';
import {
  ChangePasswordPayload,
  GetProfileResponse,
  LoginPayload,
  ResetPasswordPayload,
  UserResponse
} from '../../models/auth.model';
import { OtpPayload, CommonResponse, OtpData } from '../../models/common.model';
import { environment } from '../../../environments/environment.development';
import { CategoryResponse } from '../../models/Options.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private storageKey = 'selectedRole';

  private selectedRoleSubject: BehaviorSubject<string>;

  otpVerificationData: BehaviorSubject<OtpData | null> =
    new BehaviorSubject<OtpData | null>(null);

  homeSearch: BehaviorSubject<string> = new BehaviorSubject<string>('');
  clearHomeSearch = new Subject<void>();

  public profileDetailsSubject = new BehaviorSubject<UserResponse | null>(null);

  profileDetails$ = this.profileDetailsSubject.asObservable();

  selectedRole$ = new BehaviorSubject<string>('user').asObservable();

  constructor(private http: HttpClient) {
    const storedRole = sessionStorage.getItem(this.storageKey) ? sessionStorage.getItem(this.storageKey) : 'user';

    this.selectedRoleSubject = new BehaviorSubject<string>(
      storedRole || 'user'
    );
    this.selectedRole$ = this.selectedRoleSubject.asObservable();
  }

  isAuthenticated(): boolean {
    const stored = sessionStorage.getItem(environment.storageKey);
    return !!stored;
  }

  triggerClearHomeSearch(): void {
    this.clearHomeSearch.next();
  }

  login<T>(payload: LoginPayload): Observable<T> {
    return this.http.post<T>(
      `${environment.base_url}/${this.selectedRoleSubject.value}/login`,
      payload
    );
  }

  forgotPassword(email: string): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${environment.base_url}/${this.selectedRoleSubject.value}/forgotPassword`,
      email
    );
  }

  sendOtp(payload: OtpData): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${environment.base_url}/${this.selectedRoleSubject.value}/sendOtp`,
      payload
    );
  }

  verifyOtp<T>(payload: OtpPayload): Observable<T> {
    return this.http.post<T>(
      `${environment.base_url}/${this.selectedRoleSubject.value}/verifyOtp`,
      payload
    );
  }

  setupProfile<T>(payload: any): Observable<T> {
    return this.http.put<T>(
      `${environment.base_url}/${this.selectedRoleSubject.value}/setupProfile`,
      payload
    );
  }

  resetPassword(
    data: ResetPasswordPayload,
    id: string
  ): Observable<CommonResponse> {
    return this.http
      .post<CommonResponse>(
        `${environment.base_url}/${this.selectedRoleSubject.value}/resetPassword/${id}`,
        data
      )
      .pipe(map(res => res));
  }

  changePassword(payload: ChangePasswordPayload): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${environment.base_url}/${this.selectedRoleSubject.value}/changePassword`,
      payload
    );
  }

  logout(): Observable<CommonResponse> {
    return this.http.get<CommonResponse>(
      `${environment.base_url}/${this.selectedRoleSubject.value}/logout`
    );
  }

  deleteAccount(): Observable<CommonResponse> {
    return this.http
      .delete<{
        data: CommonResponse;
      }>(
        `${environment.base_url}/${this.selectedRoleSubject.value}/deleteProfile`
      )
      .pipe(map(res => res.data));
  }

  socialLogin(payload: CommonResponse): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${environment.base_url}user/socialLogin`,
      payload
    );
  }

  getProfile(): Observable<GetProfileResponse> {
    return this.http
      .get<{
        data: GetProfileResponse;
      }>(`${environment.base_url}/${this.selectedRoleSubject.value}/getProfile`)
      .pipe(map(res => res.data));
  }

  updateProfile(payload: UserResponse): Observable<UserResponse> {
    return this.http
      .put<{
        data: UserResponse;
      }>(
        `${environment.base_url}/${this.selectedRoleSubject.value}/updateProfile`,
        payload
      )
      .pipe(map(res => res.data));
  }

  fetchCategories(): Observable<CategoryResponse> {
    return this.http
      .get<{
        data: CategoryResponse;
      }>(`${environment.base_url}/${this.selectedRoleSubject.value}/category`)
      .pipe(map(res => res.data));
  }

  fetchSubCategories(query: string): Observable<CategoryResponse> {
    return this.http
      .get<{
        data: CategoryResponse;
      }>(
        `${environment.base_url}/${this.selectedRoleSubject.value}/subCategory${query}`
      )
      .pipe(map(res => res.data));
  }

  get currentProfileDetails(): UserResponse | null {
    return this.profileDetailsSubject.value;
  }

  setSelectedRole(role: string): void {
    sessionStorage.setItem(this.storageKey, role);
    this.selectedRoleSubject.next(role);
  }

  getSelectedRole(): string {
    return this.selectedRoleSubject.value;
  }
}
